import { useState } from 'react'
import VisaoDonoSimples from '@/pages/VisaoDonoSimples'
import { useQuery } from '@tanstack/react-query'
import {
  Calendar, Download, TrendingUp, TrendingDown, Minus, Eye, X,
  DollarSign, Package, CreditCard, Receipt, AlertCircle, Lightbulb,
  FileSpreadsheet, Filter, ChevronRight, Loader2, Activity, ArrowRightCircle,
} from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Area, AreaChart } from 'recharts'
import { api, formatMoney } from '@/lib/api'

interface Linha {
  periodo: string
  venda: number
  qtd_pedidos: number
  custo: number
  margem: number
  margem_pct: number
  recebido: number
  pago: number
  a_receber: number
  a_pagar: number
  saldo_caixa: number
  resultado_operacional: number
  saldo_acumulado: number
}

interface VisaoDono {
  periodo: { inicio: string; fim: string; agrupar: string }
  linhas: Linha[]
  totais: any
  insights: string[]
}

type DrillTipo = 'vendas' | 'custo' | 'entrada' | 'saida' | null

function hojeISO() { return new Date().toISOString().slice(0, 10) }
function inicioMesISO() {
  const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10)
}
function addDias(iso: string, dias: number) {
  const d = new Date(iso); d.setDate(d.getDate() + dias); return d.toISOString().slice(0, 10)
}

export default function VisaoDono() {
  const [inicio, setInicio] = useState(inicioMesISO())
  const [fim, setFim] = useState(hojeISO())
  const [agrupar, setAgrupar] = useState<'dia' | 'semana' | 'mes'>('dia')
  const [ocultarZerados, setOcultarZerados] = useState(true)
  const [tab, setTab] = useState<'detalhada' | 'simples'>('detalhada')
  const [drillPeriodo, setDrillPeriodo] = useState<string | null>(null)
  const [drillTipo, setDrillTipo] = useState<DrillTipo>(null)

  const { data, isLoading, refetch } = useQuery<VisaoDono>({
    queryKey: ['visao-dono', inicio, fim, agrupar],
    queryFn: async () => (await api.get('/analytics/visao-dono', { params: { inicio, fim, agrupar } })).data,
  })

  function applyPreset(preset: string) {
    const hoje = hojeISO()
    if (preset === 'hoje') { setInicio(hoje); setFim(hoje) }
    else if (preset === '7d') { setInicio(addDias(hoje, -6)); setFim(hoje) }
    else if (preset === '30d') { setInicio(addDias(hoje, -29)); setFim(hoje) }
    else if (preset === 'mes') { setInicio(inicioMesISO()); setFim(hoje) }
    else if (preset === 'mes_ant') {
      const d = new Date(); d.setDate(1); d.setDate(d.getDate() - 1)
      const fim = d.toISOString().slice(0, 10)
      d.setDate(1)
      setInicio(d.toISOString().slice(0, 10)); setFim(fim)
    }
  }

  function exportCSV() {
    if (!data) return
    const header = ['Periodo', 'Faturamento', 'Pedidos', 'Custo', 'Margem', 'Margem%', 'Recebido', 'Pago', 'A Receber', 'A Pagar', 'Saldo Caixa', 'Result Operacional', 'Saldo Acumulado']
    const rows = data.linhas.map(l => [
      l.periodo, l.venda, l.qtd_pedidos, l.custo, l.margem, l.margem_pct,
      l.recebido, l.pago, l.a_receber, l.a_pagar, l.saldo_caixa, l.resultado_operacional, l.saldo_acumulado
    ])
    const csv = [header, ...rows].map(r => r.map(v => `"${v}"`).join(';')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `visao-dono-${inicio}-a-${fim}.csv`
    a.click()
  }

  const linhasVisiveis = ocultarZerados && data
    ? data.linhas.filter(l => l.venda > 0 || l.pago > 0 || l.recebido > 0 || l.a_pagar > 0 || l.a_receber > 0)
    : data?.linhas || []

  const serieGrafico = data?.linhas.map(l => ({
    dia: l.periodo,
    saldo_acumulado: l.saldo_acumulado,
    resultado: l.resultado_operacional,
  })) || []

  return (
    <div className="p-4 lg:p-6 space-y-4">
      {/* Cabeçalho */}
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-ink-900 flex items-center gap-2">
            <Receipt size={22} /> Visão do Dono
          </h1>
          <p className="text-sm text-ink-500">
            Resultado operacional por período. Cada valor é clicável pra ver detalhe.
          </p>
        </div>
        <button onClick={exportCSV} className="btn-secondary">
          <FileSpreadsheet size={14} /> Exportar CSV
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-ink-200">
        <button
          onClick={() => setTab('detalhada')}
          className={`px-4 py-2 text-sm font-medium transition ${
            tab === 'detalhada'
              ? 'text-brand-700 border-b-2 border-brand-600 -mb-[2px]'
              : 'text-ink-500 hover:text-ink-700'
          }`}
        >
          📊 Detalhada
        </button>
        <button
          onClick={() => setTab('simples')}
          className={`px-4 py-2 text-sm font-medium transition ${
            tab === 'simples'
              ? 'text-brand-700 border-b-2 border-brand-600 -mb-[2px]'
              : 'text-ink-500 hover:text-ink-700'
          }`}
        >
          🗒️ Simples (caderninho)
        </button>
      </div>

      {tab === 'simples' && <VisaoDonoSimples inicio={inicio} fim={fim} />}

      {tab === 'detalhada' && <>

      {/* Filtros */}
      <div className="card p-3 flex items-center gap-3 flex-wrap">
        <Filter size={14} className="text-ink-500" />

        <div className="flex items-center gap-2">
          <label className="text-xs text-ink-600">De</label>
          <input type="date" value={inicio} onChange={e => setInicio(e.target.value)}
            className="input px-2 py-1 text-sm w-36" />
          <label className="text-xs text-ink-600">até</label>
          <input type="date" value={fim} onChange={e => setFim(e.target.value)}
            className="input px-2 py-1 text-sm w-36" />
        </div>

        <div className="flex gap-1">
          {[
            { k: 'hoje', l: 'Hoje' },
            { k: '7d', l: '7 dias' },
            { k: '30d', l: '30 dias' },
            { k: 'mes', l: 'Mês atual' },
            { k: 'mes_ant', l: 'Mês anterior' },
          ].map(p => (
            <button key={p.k} onClick={() => applyPreset(p.k)}
              className="text-xs px-2 py-1 rounded bg-ink-100 hover:bg-ink-200 text-ink-700">
              {p.l}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <label className="text-xs text-ink-600">Agrupar</label>
          <select value={agrupar} onChange={e => setAgrupar(e.target.value as any)}
            className="input px-2 py-1 text-sm">
            <option value="dia">Dia</option>
            <option value="semana">Semana</option>
            <option value="mes">Mês</option>
          </select>
        </div>

        <label className="flex items-center gap-1 text-xs text-ink-600 cursor-pointer">
          <input type="checkbox" checked={ocultarZerados} onChange={e => setOcultarZerados(e.target.checked)} />
          Ocultar dias sem movimento
        </label>
      </div>

      {isLoading && <div className="p-8 text-center"><Loader2 className="inline animate-spin" /> Carregando...</div>}

      {data && (
        <>
          {/* Resumo do período */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <ResumoCard
              label="Faturamento"
              valor={data.totais.venda}
              sub={`${data.totais.qtd_pedidos} pedidos`}
              icon={DollarSign} cor="emerald"
            />
            <ResumoCard
              label="Custo dos produtos"
              valor={data.totais.custo}
              sub="Baixa de estoque"
              icon={Package} cor="orange" negativo
            />
            <ResumoCard
              label="Margem bruta"
              valor={data.totais.margem}
              sub={`${data.totais.margem_pct}% do faturamento`}
              icon={TrendingUp}
              cor={data.totais.margem >= 0 ? 'blue' : 'red'}
            />
            <ResumoCard
              label="Resultado operacional"
              valor={data.totais.resultado_operacional}
              sub={`Saldo caixa real: ${formatMoney(data.totais.saldo_caixa)}`}
              icon={Activity}
              cor={data.totais.resultado_operacional >= 0 ? 'violet' : 'red'}
            />
          </div>

          {/* Gráfico saldo acumulado */}
          {serieGrafico.length > 1 && (
            <div className="card p-4">
              <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                <TrendingUp size={14} /> Saldo de caixa acumulado
              </h3>
              <div style={{ height: 220 }}>
                <ResponsiveContainer>
                  <AreaChart data={serieGrafico}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="dia" tick={{ fontSize: 10 }}
                      tickFormatter={(v) => new Date(v).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `R$${v}`} />
                    <Tooltip
                      formatter={(v: any) => [formatMoney(v), 'Acumulado']}
                      labelFormatter={(v) => new Date(v).toLocaleDateString('pt-BR')}
                    />
                    <Area type="monotone" dataKey="saldo_acumulado" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.15} strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Tabela principal */}
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-ink-50 border-b text-xs text-ink-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Período</th>
                    <th className="text-right px-3 py-2 font-semibold">Faturamento</th>
                    <th className="text-right px-3 py-2 font-semibold">Pedidos</th>
                    <th className="text-right px-3 py-2 font-semibold">Custo</th>
                    <th className="text-right px-3 py-2 font-semibold">Margem</th>
                    <th className="text-right px-3 py-2 font-semibold">%</th>
                    <th className="text-right px-3 py-2 font-semibold">Recebido</th>
                    <th className="text-right px-3 py-2 font-semibold">Pago</th>
                    <th className="text-right px-3 py-2 font-semibold">A Receber</th>
                    <th className="text-right px-3 py-2 font-semibold">A Pagar</th>
                    <th className="text-right px-3 py-2 font-semibold bg-blue-50">Saldo Caixa</th>
                    <th className="text-right px-3 py-2 font-semibold bg-violet-50">Result. Oper.</th>
                    <th className="text-right px-3 py-2 font-semibold">Acum.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {linhasVisiveis.map(l => (
                    <tr key={l.periodo} className="hover:bg-ink-50">
                      <td className="px-3 py-1.5 font-mono text-xs">{formatPeriodo(l.periodo, agrupar)}</td>
                      <td className="px-3 py-1.5 text-right">
                        <Celula valor={l.venda} onClick={l.venda > 0 ? () => { setDrillPeriodo(l.periodo); setDrillTipo('vendas') } : undefined} />
                      </td>
                      <td className="px-3 py-1.5 text-right text-xs text-ink-500">{l.qtd_pedidos || '—'}</td>
                      <td className="px-3 py-1.5 text-right">
                        <Celula valor={l.custo} negativo onClick={l.custo > 0 ? () => { setDrillPeriodo(l.periodo); setDrillTipo('custo') } : undefined} />
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <Celula valor={l.margem} colorir />
                      </td>
                      <td className="px-3 py-1.5 text-right text-xs font-semibold" style={{
                        color: l.margem_pct > 30 ? '#059669' : l.margem_pct > 15 ? '#0891b2' : l.margem_pct > 0 ? '#d97706' : '#dc2626'
                      }}>
                        {l.venda > 0 ? `${l.margem_pct}%` : '—'}
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <Celula valor={l.recebido} onClick={l.recebido > 0 ? () => { setDrillPeriodo(l.periodo); setDrillTipo('entrada') } : undefined} />
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <Celula valor={l.pago} negativo onClick={l.pago > 0 ? () => { setDrillPeriodo(l.periodo); setDrillTipo('saida') } : undefined} />
                      </td>
                      <td className="px-3 py-1.5 text-right text-xs">
                        <Celula valor={l.a_receber} sutil />
                      </td>
                      <td className="px-3 py-1.5 text-right text-xs">
                        <Celula valor={l.a_pagar} sutil negativo />
                      </td>
                      <td className="px-3 py-1.5 text-right bg-blue-50/40">
                        <Celula valor={l.saldo_caixa} colorir negrito />
                      </td>
                      <td className="px-3 py-1.5 text-right bg-violet-50/40">
                        <Celula valor={l.resultado_operacional} colorir negrito />
                      </td>
                      <td className="px-3 py-1.5 text-right text-xs font-mono text-ink-600">
                        {formatMoney(l.saldo_acumulado)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-ink-100 border-t-2 border-ink-300 font-bold text-xs">
                  <tr>
                    <td className="px-3 py-2">TOTAL</td>
                    <td className="px-3 py-2 text-right">{formatMoney(data.totais.venda)}</td>
                    <td className="px-3 py-2 text-right">{data.totais.qtd_pedidos}</td>
                    <td className="px-3 py-2 text-right text-orange-700">({formatMoney(data.totais.custo)})</td>
                    <td className="px-3 py-2 text-right text-blue-700">{formatMoney(data.totais.margem)}</td>
                    <td className="px-3 py-2 text-right">{data.totais.margem_pct}%</td>
                    <td className="px-3 py-2 text-right text-emerald-700">{formatMoney(data.totais.recebido)}</td>
                    <td className="px-3 py-2 text-right text-red-700">({formatMoney(data.totais.pago)})</td>
                    <td className="px-3 py-2 text-right text-ink-600">{formatMoney(data.totais.a_receber)}</td>
                    <td className="px-3 py-2 text-right text-ink-600">({formatMoney(data.totais.a_pagar)})</td>
                    <td className="px-3 py-2 text-right bg-blue-100">
                      <span className={data.totais.saldo_caixa >= 0 ? 'text-emerald-700' : 'text-red-700'}>
                        {formatMoney(data.totais.saldo_caixa)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right bg-violet-100">
                      <span className={data.totais.resultado_operacional >= 0 ? 'text-violet-700' : 'text-red-700'}>
                        {formatMoney(data.totais.resultado_operacional)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">—</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Insights */}
          {data.insights.length > 0 && (
            <div className="card p-4 border-amber-200 bg-amber-50/30">
              <h3 className="font-semibold text-sm flex items-center gap-2 mb-2 text-amber-900">
                <Lightbulb size={14} /> Insights
              </h3>
              <ul className="space-y-1">
                {data.insights.map((i, idx) => (
                  <li key={idx} className="text-sm text-amber-900 flex items-start gap-2">
                    <ChevronRight size={12} className="mt-1 flex-shrink-0" />
                    <span>{i}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Legenda */}
          <div className="card p-3 text-xs text-ink-500">
            <p className="font-semibold mb-1">📚 Como ler este relatório:</p>
            <ul className="space-y-0.5 ml-2">
              <li><strong>Faturamento</strong>: total faturado no dia (pedidos confirmados ou adiante)</li>
              <li><strong>Custo</strong>: baixa de estoque valorada (quantidade × preço de custo)</li>
              <li><strong>Margem</strong>: Faturamento − Custo (quanto sobrou do que foi vendido)</li>
              <li><strong>Recebido/Pago</strong>: movimentações em conta (financeiro conciliado)</li>
              <li><strong>A Receber/A Pagar</strong>: lançamentos com data do dia mas não conciliados</li>
              <li className="bg-blue-50 px-1 rounded"><strong>Saldo Caixa</strong>: Recebido − Pago (caixa real do dia)</li>
              <li className="bg-violet-50 px-1 rounded"><strong>Resultado Operacional</strong>: Margem + (A Receber − A Pagar) — visão com futuros</li>
            </ul>
          </div>
        </>
      )}

      {/* Drill-down modal */}
      {drillPeriodo && drillTipo && (
        <DrillModal periodo={drillPeriodo} tipo={drillTipo}
          onClose={() => { setDrillPeriodo(null); setDrillTipo(null) }} />
      )}
      </>}
    </div>
  )
}

// ============ COMPONENTES ============

function ResumoCard({ label, valor, sub, icon: Icon, cor, negativo }: any) {
  const cores: Record<string, string> = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    violet: 'bg-violet-50 text-violet-700 border-violet-200',
    red: 'bg-red-50 text-red-700 border-red-200',
  }
  return (
    <div className="card p-3">
      <div className="flex items-center gap-2 mb-1">
        <div className={`p-1.5 rounded border ${cores[cor]}`}><Icon size={14} /></div>
        <span className="text-xs font-semibold uppercase text-ink-500">{label}</span>
      </div>
      <p className={`text-xl font-bold ${negativo ? 'text-orange-700' : 'text-ink-900'}`}>
        {negativo ? `(${formatMoney(valor)})` : formatMoney(valor)}
      </p>
      <p className="text-xs text-ink-500 truncate">{sub}</p>
    </div>
  )
}

function Celula({ valor, negativo, colorir, negrito, sutil, onClick }: any) {
  if (!valor) return <span className="text-ink-300">—</span>
  const texto = negativo ? `(${formatMoney(valor)})` : formatMoney(valor)
  const color = negativo ? 'text-red-700'
    : colorir ? (valor >= 0 ? 'text-emerald-700' : 'text-red-700')
    : sutil ? 'text-ink-500'
    : 'text-ink-800'
  const weight = negrito ? 'font-bold' : 'font-medium'
  if (onClick) {
    return (
      <button onClick={onClick} className={`${color} ${weight} hover:underline cursor-pointer font-mono text-xs`}>
        {texto}
      </button>
    )
  }
  return <span className={`${color} ${weight} font-mono text-xs`}>{texto}</span>
}

function formatPeriodo(iso: string, agrupar: string): string {
  const d = new Date(iso + 'T12:00')
  if (agrupar === 'mes') return d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })
  if (agrupar === 'semana') return 'Sem ' + d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
  return d.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' })
}

// ============ DRILL-DOWN MODAL ============

function DrillModal({ periodo, tipo, onClose }: { periodo: string; tipo: DrillTipo; onClose: () => void }) {
  const titulos = {
    vendas: { titulo: 'Vendas do dia', endpoint: `/analytics/visao-dono/detalhe/vendas?data=${periodo}` },
    custo: { titulo: 'Custo de estoque (itens vendidos)', endpoint: `/analytics/visao-dono/detalhe/custo?data=${periodo}` },
    entrada: { titulo: 'Lançamentos recebidos', endpoint: `/analytics/visao-dono/detalhe/financeiro?data=${periodo}&tipo=entrada` },
    saida: { titulo: 'Lançamentos pagos', endpoint: `/analytics/visao-dono/detalhe/financeiro?data=${periodo}&tipo=saida` },
  } as const
  const cfg = titulos[tipo as keyof typeof titulos]

  const { data, isLoading } = useQuery<any[]>({
    queryKey: ['drill', periodo, tipo],
    queryFn: async () => (await api.get(cfg.endpoint)).data,
    enabled: !!tipo,
  })

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-2xl bg-white z-50 shadow-2xl overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white border-b px-5 py-3 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-lg">{cfg.titulo}</h2>
            <p className="text-sm text-ink-500">{new Date(periodo + 'T12:00').toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-ink-100"><X size={18} /></button>
        </div>

        <div className="p-4">
          {isLoading && <div className="text-center p-8"><Loader2 className="inline animate-spin" /> Carregando...</div>}

          {!isLoading && data && data.length === 0 && (
            <p className="text-center text-ink-500 py-10">Sem registros neste dia</p>
          )}

          {!isLoading && data && data.length > 0 && (
            <>
              {tipo === 'vendas' && <TabelaVendas itens={data} />}
              {tipo === 'custo' && <TabelaCusto itens={data} />}
              {(tipo === 'entrada' || tipo === 'saida') && <TabelaFinanceiro itens={data} tipo={tipo} />}
            </>
          )}
        </div>
      </div>
    </>
  )
}

function TabelaVendas({ itens }: any) {
  const total = itens.reduce((s: number, i: any) => s + i.total, 0)
  return (
    <div>
      <table className="w-full text-sm">
        <thead className="bg-ink-50 text-xs">
          <tr><th className="text-left p-2">Pedido</th><th className="text-left p-2">Cliente</th><th className="text-left p-2">Canal</th><th className="text-left p-2">Status</th><th className="text-right p-2">Valor</th></tr>
        </thead>
        <tbody className="divide-y">
          {itens.map((p: any) => (
            <tr key={p.id} className="hover:bg-ink-50">
              <td className="p-2 font-mono text-xs">#{p.numero}</td>
              <td className="p-2">{p.cliente_nome || '—'}</td>
              <td className="p-2 text-xs text-ink-500">{p.canal}</td>
              <td className="p-2"><span className="text-[10px] uppercase bg-ink-100 px-1.5 py-0.5 rounded">{p.status}</span></td>
              <td className="p-2 text-right font-semibold">{formatMoney(p.total)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-ink-100 font-bold">
          <tr><td colSpan={4} className="p-2">TOTAL {itens.length} pedido(s)</td><td className="p-2 text-right">{formatMoney(total)}</td></tr>
        </tfoot>
      </table>
    </div>
  )
}

function TabelaCusto({ itens }: any) {
  const totalCusto = itens.reduce((s: number, i: any) => s + i.custo_total, 0)
  const totalVenda = itens.reduce((s: number, i: any) => s + i.venda_total, 0)
  const margem = totalVenda - totalCusto
  return (
    <div>
      <div className="bg-blue-50 border border-blue-200 rounded p-2 mb-3 text-xs text-blue-900">
        <strong>Auditoria:</strong> cada linha = 1 item vendido no dia. Custo = quantidade × preço_custo cadastrado no produto.
      </div>
      <table className="w-full text-sm">
        <thead className="bg-ink-50 text-xs">
          <tr><th className="text-left p-2">Pedido</th><th className="text-left p-2">Produto</th><th className="text-right p-2">Qtd</th><th className="text-right p-2">Custo un.</th><th className="text-right p-2">Custo tot.</th><th className="text-right p-2">Venda</th></tr>
        </thead>
        <tbody className="divide-y">
          {itens.map((i: any) => (
            <tr key={i.id} className="hover:bg-ink-50">
              <td className="p-2 font-mono text-xs">#{i.pedido_numero}</td>
              <td className="p-2">
                <span className="font-mono text-xs text-ink-500">{i.codigo}</span> {i.descricao}
              </td>
              <td className="p-2 text-right">{i.quantidade}</td>
              <td className="p-2 text-right text-xs text-ink-500">{formatMoney(i.preco_custo)}</td>
              <td className="p-2 text-right text-orange-700 font-semibold">({formatMoney(i.custo_total)})</td>
              <td className="p-2 text-right text-emerald-700">{formatMoney(i.venda_total)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-ink-100 font-bold">
          <tr>
            <td colSpan={4} className="p-2">TOTAL</td>
            <td className="p-2 text-right text-orange-700">({formatMoney(totalCusto)})</td>
            <td className="p-2 text-right text-emerald-700">{formatMoney(totalVenda)}</td>
          </tr>
          <tr>
            <td colSpan={4} className="p-2">Margem do dia</td>
            <td colSpan={2} className={`p-2 text-right ${margem >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
              {formatMoney(margem)} ({totalVenda > 0 ? `${((margem / totalVenda) * 100).toFixed(1)}%` : '—'})
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

function TabelaFinanceiro({ itens, tipo }: any) {
  const total = itens.reduce((s: number, i: any) => s + i.valor, 0)
  const isEntrada = tipo === 'entrada'
  return (
    <div>
      <table className="w-full text-sm">
        <thead className="bg-ink-50 text-xs">
          <tr><th className="text-left p-2">Descrição</th><th className="text-left p-2">Pessoa</th><th className="text-left p-2">Conta</th><th className="text-left p-2">Origem</th><th className="text-center p-2">Conc.</th><th className="text-right p-2">Valor</th></tr>
        </thead>
        <tbody className="divide-y">
          {itens.map((l: any) => (
            <tr key={l.id} className="hover:bg-ink-50">
              <td className="p-2">{l.descricao}</td>
              <td className="p-2 text-xs text-ink-600">{l.pessoa_nome || '—'}</td>
              <td className="p-2 text-xs text-ink-600">{l.conta_nome || '—'}</td>
              <td className="p-2 text-xs text-ink-500">{l.origem}</td>
              <td className="p-2 text-center">{l.conciliado ? '✓' : '—'}</td>
              <td className={`p-2 text-right font-semibold ${isEntrada ? 'text-emerald-700' : 'text-red-700'}`}>
                {isEntrada ? formatMoney(l.valor) : `(${formatMoney(l.valor)})`}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-ink-100 font-bold">
          <tr><td colSpan={5} className="p-2">TOTAL {itens.length} lançamento(s)</td>
            <td className={`p-2 text-right ${isEntrada ? 'text-emerald-700' : 'text-red-700'}`}>
              {isEntrada ? formatMoney(total) : `(${formatMoney(total)})`}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}
